// Import model
import { Room } from '../models/Room';

// Import base class serializer
import { EntityOwnedModelSerializer } from './EntityOwnedModelSerializer';

// Import dependency types (models and enums used in properties)
import { SfRoom } from '../models/SfRoom';

// Import dependency serializers (for complex types)
import { SfRoomSerializer } from './SfRoomSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for Room - handles JSON serialization and deserialization with type validation
 */
export class RoomSerializer {
  /**
   * Deserializes a JSON string into a Room instance
   */
  static deserialize(json: string): Room {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Room instance
   */
  static fromObject(obj: any): Room {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Room');
    }


    const instance: Room = {
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      externalId: obj['externalId'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      properties: obj['properties'] ? SfRoomSerializer.fromObject(obj['properties']) : undefined as any,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Room instances
   */
  static deserializeArray(json: string): Room[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Room instance to a JSON string
   */
  static serialize(instance: Room): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Room instance to a plain object
   */
  static toObject(instance: Room): any {
    return {
      'name': instance.name,
      'description': instance.description,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'externalId': instance.externalId,
      'isActive': instance.isActive,
      'properties': instance.properties ? SfRoomSerializer.toObject(instance.properties) : undefined,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
    };
  }

  /**
   * Serializes an array of Room instances to a JSON string
   */
  static serializeArray(instances: Room[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
